/**
 * My Classes Page
 * All users can access (students, instructors, admins).
 * Shows only classes the current user is personally enrolled in.
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useEnrollment } from '../hooks/useEnrollment';
import { useLabs } from '../hooks/useLabs';
import { useBeacon } from '../hooks/useBeacon';
import { apiGet } from '../services/api';
import {
  Loader2,
  AlertCircle,
  ExternalLink,
  Wifi,
  RefreshCw,
  Shield,
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  CalendarClock,
  Clock,
} from 'lucide-react';
import type { StudentLab, LabInstanceStatus } from '@cloak-next/shared';
import { formatDate } from '@cloak-next/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(isoString: string): string {
  const diff = new Date(isoString).getTime() - Date.now();
  const absDiff = Math.abs(diff);
  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);

  let label: string;
  if (days >= 1) label = `${days} day${days !== 1 ? 's' : ''}`;
  else if (hours >= 1) label = `${hours} hour${hours !== 1 ? 's' : ''}`;
  else if (minutes >= 1) label = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  else label = 'moments';

  return diff > 0 ? `in ${label}` : `${label} ago`;
}

function classDisplayName(lab: StudentLab): string {
  return lab.classTitle || lab.className;
}

// ---------------------------------------------------------------------------
// Getting-started panel state
// ---------------------------------------------------------------------------

interface GettingStartedState {
  open: boolean;
  content: string | null;
  loading: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function IpStatusBanner({
  ipStatus,
  currentIp,
  currentCidr,
  beaconMessage,
  beaconLoading,
  resetIpDetection,
  onClearIp,
  onRegisterIp,
  hasActiveLabs,
}: {
  ipStatus: string;
  currentIp: string | null;
  currentCidr: string | null;
  beaconMessage: string | null;
  beaconLoading: boolean;
  resetIpDetection: () => Promise<void>;
  onClearIp: () => Promise<void>;
  onRegisterIp: () => Promise<void>;
  hasActiveLabs: boolean;
}) {
  if (ipStatus === 'unknown' || (ipStatus === 'pending' && !hasActiveLabs)) return null;

  if (ipStatus === 'vpn-detected') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-amber-800">VPN Detected</h4>
          <p className="text-sm text-amber-700 mt-1">
            {beaconMessage || 'Your IP address is changing across different networks. Please disable any VPN for stable lab access.'}
          </p>
          <button
            onClick={resetIpDetection}
            disabled={beaconLoading}
            className="mt-3 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
          >
            {beaconLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Reset IP Detection
          </button>
        </div>
      </div>
    );
  }

  if (ipStatus === 'stable') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
        <Shield className="h-5 w-5 text-green-600 flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm text-green-800">
            <span className="font-medium">IP Access Enabled</span>
            {currentIp && <span className="text-green-600 ml-2">({currentIp})</span>}
          </span>
        </div>
        <Wifi className="h-4 w-4 text-green-500 flex-shrink-0" />
        <button
          onClick={onClearIp}
          disabled={beaconLoading}
          className="px-3 py-1 text-xs border border-green-400 text-green-700 rounded hover:bg-green-100 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
        >
          {beaconLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Clear My IP
        </button>
      </div>
    );
  }

  if (ipStatus === 'campus-network') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
        <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm text-blue-800">
            <span className="font-medium">Campus Network Detected</span>
            {currentCidr && <span className="text-blue-600 ml-2">(Network: {currentCidr})</span>}
          </span>
        </div>
        <Wifi className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <button
          onClick={onClearIp}
          disabled={beaconLoading}
          className="px-3 py-1 text-xs border border-blue-400 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
        >
          {beaconLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Clear My IP
        </button>
      </div>
    );
  }

  if (ipStatus === 'pending') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3">
        {beaconLoading
          ? <Loader2 className="h-5 w-5 text-gray-500 animate-spin flex-shrink-0" />
          : <Wifi className="h-5 w-5 text-gray-400 flex-shrink-0" />}
        <span className="text-sm text-gray-600 flex-1">
          {beaconLoading ? 'Registering IP...' : 'No IP registered.'}
        </span>
        {!beaconLoading && (
          <button
            onClick={onRegisterIp}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 flex-shrink-0"
          >
            <Shield className="h-4 w-4" />
            Force Register IP
          </button>
        )}
      </div>
    );
  }

  return null;
}

function ClassCard({
  lab,
  gettingStarted,
  onToggleGettingStarted,
}: {
  lab: StudentLab;
  gettingStarted: GettingStartedState | undefined;
  onToggleGettingStarted: (instanceId: string) => void;
}) {
  const title = classDisplayName(lab);
  const startFormatted = formatDate(lab.startTime, false);
  const endFormatted = formatDate(lab.endTime, false);
  const endMs = new Date(lab.endTime).getTime();

  // ── Active class ──────────────────────────────────────────────────────────
  if (lab.classStatus === 'active') {
    const lab0 = lab.labs[0];
    const status = lab0?.status as LabInstanceStatus | undefined;

    let borderColor = 'border-l-amber-400';
    let bgColor = '';
    if (status === 'ready') { borderColor = 'border-l-green-500'; bgColor = ''; }
    if (status === 'failed') { borderColor = 'border-l-red-500'; }

    return (
      <div className={`border rounded-lg p-5 border-l-4 ${borderColor} ${bgColor} space-y-3`}>
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
              <h3 className="font-semibold text-base">{title}</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Instructor: {lab.instructorEmail}
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground shrink-0">
            <div className="flex items-center gap-1 justify-end">
              <Clock className="h-3 w-3" />
              <span>Ends {relativeTime(lab.endTime)}</span>
            </div>
            <div className="mt-0.5">
              {startFormatted} – {endFormatted}
            </div>
          </div>
        </div>

        {/* Lab access */}
        {!lab.instanceId && !lab.labsProvisioned && (
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            Waiting for instructor to provision labs…
          </p>
        )}

        {!lab.instanceId && lab.labsProvisioned && (
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            No lab instance available — contact your instructor.
          </p>
        )}

        {lab.instanceId && lab.labs.length === 0 && (
          <p className="text-sm text-muted-foreground">No lab environments configured for this class.</p>
        )}

        {lab.instanceId && lab.labs.length > 0 && (() => {
          if (status === 'ready') {
            const instanceId = lab.instanceId;
            const gs = gettingStarted;
            return (
              <div className="space-y-2">
                {lab.dashboardUrl && (
                  <a
                    href={lab.dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    Open Lab Dashboard
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                <div>
                  <button
                    onClick={() => onToggleGettingStarted(instanceId)}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <BookOpen className="h-4 w-4" />
                    Getting Started
                    {gs?.open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {gs?.open && (
                    <div className="mt-2 border rounded p-4">
                      {gs.loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{gs.content || ''}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          if (status === 'provisioning') {
            return (
              <div className="px-3 py-2 text-sm border rounded bg-amber-50 text-amber-700 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Lab environment is provisioning (typically 10–15 minutes)…
              </div>
            );
          }

          if (status === 'failed') {
            return (
              <div className="px-3 py-2 text-sm border rounded bg-red-50 text-red-700 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                Lab setup failed — contact your instructor.
              </div>
            );
          }

          return (
            <div className="px-3 py-2 text-sm border rounded bg-gray-50 text-gray-500">
              Lab status: {status}
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Scheduled (upcoming) ──────────────────────────────────────────────────
  if (lab.classStatus === 'scheduled') {
    return (
      <div className="border rounded-lg p-5 border-l-4 border-l-blue-300 space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Upcoming
              </span>
              <h3 className="font-semibold text-base">{title}</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Instructor: {lab.instructorEmail}
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground shrink-0">
            <div className="flex items-center gap-1 justify-end">
              <CalendarClock className="h-3 w-3" />
              <span>Starts {relativeTime(lab.startTime)}</span>
            </div>
            <div className="mt-0.5">
              {startFormatted} – {endFormatted}
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Labs will be available when the class starts.
        </p>
      </div>
    );
  }

  // ── Draft ─────────────────────────────────────────────────────────────────
  if (lab.classStatus === 'draft') {
    return (
      <div className="border rounded-lg p-5 border-l-4 border-l-gray-300 space-y-2 opacity-70">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            Not Available
          </span>
          <h3 className="font-semibold text-base">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">This class is not yet available. Contact your instructor.</p>
      </div>
    );
  }

  // ── Ended ─────────────────────────────────────────────────────────────────
  // (rendered separately in the collapsed section)
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded text-sm text-muted-foreground">
      <span>{title}</span>
      <span className="text-xs">Ended {endMs > 0 ? relativeTime(lab.endTime) : endFormatted}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enroll form
// ---------------------------------------------------------------------------

function EnrollForm({
  compact,
  classCode,
  setClassCode,
  onSubmit,
  joinLoading,
  joinError,
  successMessage,
}: {
  compact: boolean;
  classCode: string;
  setClassCode: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  joinLoading: boolean;
  joinError: string | null;
  successMessage: string;
}) {
  return (
    <div className={compact ? '' : 'border rounded-lg p-6 space-y-4'}>
      {!compact && (
        <>
          <h3 className="text-xl font-semibold">Join a Class</h3>
          <p className="text-sm text-muted-foreground">
            Enter the class code provided by your instructor to enroll.
          </p>
        </>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded text-sm">
          {successMessage}
        </div>
      )}
      {joinError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
          {joinError}
        </div>
      )}
      <form onSubmit={onSubmit} className={`flex gap-2 ${compact ? 'max-w-lg' : 'max-w-sm'}`}>
        {compact && (
          <span className="text-sm text-muted-foreground self-center whitespace-nowrap">
            Have another class code?
          </span>
        )}
        <input
          type="text"
          placeholder="Enter class code"
          value={classCode}
          onChange={(e) => setClassCode(e.target.value)}
          className="flex-1 px-3 py-2 border rounded-md text-sm"
          disabled={joinLoading}
          required
        />
        <button
          type="submit"
          disabled={joinLoading || !classCode}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {joinLoading ? 'Enrolling…' : 'Enroll'}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function StudentDashboard() {
  const { joinClass, loading: joinLoading, error: joinError } = useEnrollment();
  const { studentLabs, loading: labsLoading, error: labsError, fetchStudentLabs } = useLabs();
  const {
    ipStatus,
    currentIp,
    currentCidr,
    message: beaconMessage,
    loading: beaconLoading,
    resetIpDetection,
    sendBeacon,
    clearIp,
  } = useBeacon();

  const [classCode, setClassCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [gettingStarted, setGettingStarted] = useState<Record<string, GettingStartedState>>({});
  const [pastExpanded, setPastExpanded] = useState(false);
  const autoExpandedRef = useRef<Set<string>>(new Set());

  // Fetch labs on mount
  useEffect(() => {
    fetchStudentLabs();
  }, [fetchStudentLabs]);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Auto-expand getting-started for active+ready labs that have no dashboard button
  useEffect(() => {
    if (!studentLabs || studentLabs.length === 0) return;
    for (const lab of studentLabs) {
      const instanceId = lab.instanceId;
      if (
        lab.classStatus === 'active' &&
        !lab.dashboardUrl &&
        instanceId &&
        lab.labs[0]?.status === 'ready' &&
        !autoExpandedRef.current.has(instanceId)
      ) {
        autoExpandedRef.current.add(instanceId);
        setGettingStarted(prev => ({ ...prev, [instanceId]: { open: true, content: null, loading: true } }));
        apiGet<{ success: boolean; data: { markdown: string } }>(`/labs/${instanceId}/getting-started`)
          .then(result => {
            setGettingStarted(prev => ({
              ...prev,
              [instanceId]: { open: true, content: result.data?.markdown || '', loading: false },
            }));
          })
          .catch(() => {
            setGettingStarted(prev => ({
              ...prev,
              [instanceId]: { open: true, content: 'Failed to load getting-started guide.', loading: false },
            }));
          });
      }
    }
  }, [studentLabs]);

  // Poll every 30s when any enrollment is provisioning
  useEffect(() => {
    if (!studentLabs || studentLabs.length === 0) return;
    const hasProvisioning = studentLabs.some(e => e.labs.some(l => l.status === 'provisioning'));
    if (!hasProvisioning) return;
    const interval = setInterval(() => fetchStudentLabs(), 30000);
    return () => clearInterval(interval);
  }, [studentLabs, fetchStudentLabs]);

  const handleJoinClass = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    if (!classCode) return;
    try {
      const result = await joinClass(classCode);
      const displayName = result.class.classTitle || result.class.className;
      setSuccessMessage(`Successfully enrolled in ${displayName}!`);
      setClassCode('');
      fetchStudentLabs();
      sendBeacon();
    } catch {
      // error shown via joinError
    }
  };

  const handleToggleGettingStarted = async (instanceId: string) => {
    const current = gettingStarted[instanceId];
    if (current?.open) {
      setGettingStarted(prev => ({ ...prev, [instanceId]: { ...current, open: false } }));
      return;
    }
    if (current?.content !== null && current?.content !== undefined) {
      setGettingStarted(prev => ({ ...prev, [instanceId]: { ...current, open: true } }));
      return;
    }
    setGettingStarted(prev => ({ ...prev, [instanceId]: { open: true, content: null, loading: true } }));
    try {
      const result = await apiGet<{ success: boolean; data: { markdown: string } }>(`/labs/${instanceId}/getting-started`);
      setGettingStarted(prev => ({
        ...prev,
        [instanceId]: { open: true, content: result.data?.markdown || '', loading: false },
      }));
    } catch {
      setGettingStarted(prev => ({
        ...prev,
        [instanceId]: { open: true, content: 'Failed to load getting-started guide.', loading: false },
      }));
    }
  };

  // ── Sort enrollments ──────────────────────────────────────────────────────
  const active = studentLabs
    .filter(l => l.classStatus === 'active')
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const scheduled = studentLabs
    .filter(l => l.classStatus === 'scheduled')
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const draft = studentLabs.filter(l => l.classStatus === 'draft');

  const ended = studentLabs
    .filter(l => l.classStatus === 'ended')
    .sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime());

  const hasActiveLabs = active.some(l => !!l.instanceId);
  const hasActiveOrUpcoming = active.length > 0 || scheduled.length > 0;


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold">My Classes</h2>
        <p className="text-muted-foreground mt-2">
          Your enrolled classes and lab environments
        </p>
      </div>

      <IpStatusBanner
        ipStatus={ipStatus}
        currentIp={currentIp}
        currentCidr={currentCidr}
        beaconMessage={beaconMessage}
        beaconLoading={beaconLoading}
        resetIpDetection={resetIpDetection}
        onClearIp={clearIp}
        onRegisterIp={sendBeacon}
        hasActiveLabs={hasActiveLabs}
      />

      {/* Loading state */}
      {labsLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your classes…
        </div>
      )}

      {/* Error state */}
      {!labsLoading && labsError && studentLabs.length === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Failed to load your classes</p>
            <p className="text-sm text-red-700 mt-1">{labsError}</p>
            <button
              onClick={fetchStudentLabs}
              className="mt-3 px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Empty state — no enrollments at all */}
      {!labsLoading && !labsError && studentLabs.length === 0 && (
        <div className="space-y-6">
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-lg font-medium mb-1">You're not enrolled in any classes yet.</p>
            <p className="text-sm">Enter a class code below to get started.</p>
          </div>
          <EnrollForm compact={false} classCode={classCode} setClassCode={setClassCode} onSubmit={handleJoinClass} joinLoading={joinLoading} joinError={joinError} successMessage={successMessage} />
        </div>
      )}

      {/* Class cards */}
      {!labsLoading && studentLabs.length > 0 && (
        <div className="space-y-4">
          {/* Active classes */}
          {active.map(lab => (
            <ClassCard
              key={lab.className}
              lab={lab}
              gettingStarted={lab.instanceId ? gettingStarted[lab.instanceId] : undefined}
              onToggleGettingStarted={handleToggleGettingStarted}
            />
          ))}

          {/* Draft classes */}
          {draft.map(lab => (
            <ClassCard
              key={lab.className}
              lab={lab}
              gettingStarted={undefined}
              onToggleGettingStarted={handleToggleGettingStarted}
            />
          ))}

          {/* Upcoming classes */}
          {scheduled.map(lab => (
            <ClassCard
              key={lab.className}
              lab={lab}
              gettingStarted={undefined}
              onToggleGettingStarted={handleToggleGettingStarted}
            />
          ))}

          {/* Past classes — collapsed */}
          {ended.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setPastExpanded(prev => !prev)}
                className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-muted-foreground hover:bg-muted/40 transition-colors"
              >
                <span>Past Classes ({ended.length})</span>
                {pastExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {pastExpanded && (
                <div className="border-t divide-y">
                  {ended.map(lab => (
                    <ClassCard
                      key={lab.className}
                      lab={lab}
                      gettingStarted={undefined}
                      onToggleGettingStarted={handleToggleGettingStarted}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Compact enroll form at bottom when already enrolled in something */}
          {hasActiveOrUpcoming && (
            <div className="pt-2">
              <EnrollForm compact={true} classCode={classCode} setClassCode={setClassCode} onSubmit={handleJoinClass} joinLoading={joinLoading} joinError={joinError} successMessage={successMessage} />
            </div>
          )}

          {/* Full enroll form when only ended classes exist */}
          {!hasActiveOrUpcoming && (
            <EnrollForm compact={false} classCode={classCode} setClassCode={setClassCode} onSubmit={handleJoinClass} joinLoading={joinLoading} joinError={joinError} successMessage={successMessage} />
          )}
        </div>
      )}
    </div>
  );
}
